import { Alert, Box, Button, Card, CardContent, Chip, Typography } from "@mui/material";
import PropTypes from "prop-types";
import UtilService from "../../../services/UtilService";
import PuzzleSolvedOriginalImage from "../common/components/PuzzleSolvedOriginalImage";
import {
    getJigsawDifficultyDisplayInfo,
    pickJigsawSquareImageUrl,
    resolveJigsawTemplateTileSize,
} from "./jigsawBrowseUtils";
import JigsawLibraryBoardPreview from "./JigsawLibraryBoardPreview";
import { JigsawDieLineOverlay } from "./JigsawDieLineOverlay";
import { browseViewButtonSx } from "../../browse-jigsaw/JigsawBrowseStoryCard";

/**
 * Library card for jigsaw puzzles.
 * In-progress: live board preview from saved positions; completed: solved image.
 */
export default function JigsawLibraryCard({
    puzzle,
    onPlayClick,
    buttonText = "Play",
    lang,
    t,
    isInProgress = false,
}) {
    const story = puzzle.story;
    const imageUrl =
        pickJigsawSquareImageUrl(story) ??
        pickJigsawSquareImageUrl(puzzle) ??
        puzzle.imageUrl ??
        null;
    const title = story?.en?.title ?? story?.english?.title ?? story?.fr?.title ?? story?.french?.title ?? "";
    const playData = puzzle.hwPlay;
    const isCompleted = playData?.finishedAt != null || playData?.isCompleted === true;
    const showLiveBoardPreview = isInProgress && !isCompleted;

    let timeTakenStr = null;
    if (isCompleted && playData?.startedAt && playData?.finishedAt) {
        timeTakenStr = UtilService.formatCompletionTime(playData.startedAt, playData.finishedAt);
    }

    let completedAgoStr = null;
    if (isCompleted && playData?.finishedAt) {
        completedAgoStr = UtilService.formatRelativeTime(playData.finishedAt);
    }

    const tileSize = puzzle.tileSize ?? resolveJigsawTemplateTileSize(puzzle.jigsaw ?? puzzle);
    const levelInfo =
        tileSize > 0 ? getJigsawDifficultyDisplayInfo(tileSize, puzzle.difficultyLevel) : null;
    const totalPieces = tileSize > 0 ? tileSize * tileSize : 0;
    const placedOnBoard = Array.isArray(puzzle.boardPositions) ? puzzle.boardPositions.length : 0;
    const bundleState = String(puzzle.state || puzzle.storyJigsaw?.state || "").toUpperCase();
    const isUnavailable =
        puzzle.disabled === true || bundleState === "DISABLED" || bundleState === "DISCARDED";
    const isDiscarded = bundleState === "DISCARDED";

    const previewNode = (() => {
        if (showLiveBoardPreview) {
            return <JigsawLibraryBoardPreview puzzle={puzzle} />;
        }
        if (!imageUrl) return null;
        if (isCompleted) {
            return (
                <Box
                    sx={{
                        width: "100%",
                        aspectRatio: "1 / 1",
                        borderRadius: 1,
                        overflow: "hidden",
                        bgcolor: "grey.900",
                    }}
                >
                    <PuzzleSolvedOriginalImage imageUrl={imageUrl} alt="" />
                </Box>
            );
        }
        return (
            <Box
                sx={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "1 / 1",
                    borderRadius: 1,
                    overflow: "hidden",
                    bgcolor: "grey.900",
                }}
            >
                <Box
                    component="img"
                    src={imageUrl}
                    alt=""
                    referrerPolicy="no-referrer"
                    sx={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
                <JigsawDieLineOverlay
                    pieces={puzzle.piecePaths?.pieces ?? puzzle.piecePaths}
                    svgPathFallback={puzzle.svgPath ?? null}
                    preserveAspectRatio="xMidYMid slice"
                    opacity={0.85}
                    zIndex={1}
                    strokeWidth={2.5}
                />
            </Box>
        );
    })();

    return (
        <Card
            variant="outlined"
            sx={{
                backgroundColor: "background.paper",
                borderColor: "divider",
                width: "100%",
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                transition: "box-shadow 0.2s, border-color 0.2s",
                "&:hover": { boxShadow: 3, borderColor: "primary.light" },
            }}
        >
            <CardContent sx={{ pt: 1.5, flex: "1 1 0%", display: "flex", flexDirection: "column", pb: 1.5 }}>
                {title && (
                    <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        sx={{
                            fontWeight: 700,
                            lineHeight: 1.35,
                            mb: 0.75,
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                        }}
                    >
                        {title}
                    </Typography>
                )}

                {previewNode ? (
                    <Box sx={{ mb: 1, position: "relative" }}>
                        <Box
                            sx={
                                isUnavailable
                                    ? { filter: "grayscale(1)", opacity: 0.72 }
                                    : undefined
                            }
                        >
                            {previewNode}
                        </Box>
                        {isUnavailable ? (
                            <Box
                                sx={{
                                    position: "absolute",
                                    inset: 0,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    bgcolor: "rgba(50,50,50,0.5)",
                                    borderRadius: 1,
                                    px: 1,
                                    pointerEvents: "none",
                                }}
                            >
                                <Typography
                                    variant="caption"
                                    sx={{ color: "common.white", fontWeight: 700, textAlign: "center" }}
                                >
                                    {t?.("puzzle.jigsaw.not-available", "Not available") || "Not available"}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{ color: "common.white", textAlign: "center" }}
                                >
                                    {isDiscarded
                                        ? t?.(
                                              "puzzle.jigsaw.unavailable-discarded",
                                              "This puzzle is no longer available.",
                                          ) || "This puzzle is no longer available."
                                        : t?.(
                                              "puzzle.jigsaw.will-be-removed-in-week",
                                              "Will be removed in 1 week",
                                          ) || "Will be removed in 1 week"}
                                </Typography>
                            </Box>
                        ) : null}
                    </Box>
                ) : null}

                {isUnavailable ? (
                    <Alert severity={isDiscarded ? "info" : "warning"} sx={{ mb: 1, py: 0.5 }}>
                        {isDiscarded
                            ? t?.(
                                  "puzzle.jigsaw.unavailable-discarded",
                                  "This puzzle is no longer available.",
                              ) || "This puzzle is no longer available."
                            : t?.(
                                  "puzzle.jigsaw.will-be-removed-in-week",
                                  "Will be removed in 1 week",
                              ) || "Will be removed in 1 week"}
                    </Alert>
                ) : null}

                {showLiveBoardPreview && totalPieces > 0 && (
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                        {t("puzzle.jigsaw.library-pieces-on-board", {
                            placed: placedOnBoard,
                            total: totalPieces,
                            defaultValue: `${placedOnBoard}/${totalPieces} on board`,
                        })}
                    </Typography>
                )}

                {isCompleted && (
                    <Box sx={{ mb: 0.75 }}>
                        {completedAgoStr && (
                            <Typography variant="caption" color="text.secondary" display="block">
                                {t("puzzle.card.completed")}: {completedAgoStr}
                            </Typography>
                        )}
                        {timeTakenStr && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.25 }}>
                                {t("puzzle.card.time-taken")}: {timeTakenStr}
                            </Typography>
                        )}
                    </Box>
                )}

                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        mt: "auto",
                        flexWrap: "nowrap",
                    }}
                >
                    {levelInfo && t ? (
                        <Chip
                            label={t(levelInfo.levelKey)}
                            size="small"
                            color={levelInfo.chipColor}
                            variant="filled"
                            sx={{ fontSize: "0.7rem", ...levelInfo.chipSx }}
                        />
                    ) : (
                        <span />
                    )}
                    <Button
                        size="small"
                        variant="contained"
                        disabled={isUnavailable}
                        onClick={() => {
                            if (isUnavailable) return;
                            onPlayClick?.(puzzle);
                        }}
                        sx={{ ...browseViewButtonSx, flexShrink: 0 }}
                    >
                        {buttonText}
                    </Button>
                </Box>
            </CardContent>
        </Card>
    );
}

JigsawLibraryCard.propTypes = {
    puzzle: PropTypes.object.isRequired,
    onPlayClick: PropTypes.func,
    buttonText: PropTypes.string,
    lang: PropTypes.string,
    t: PropTypes.func,
    isInProgress: PropTypes.bool,
};
